import React, { useState, useEffect, Suspense, lazy } from "react";
import { Modal, Descriptions, Tag, Space, Typography, Spin, message, Divider, Image, Empty, Timeline, Tabs } from "antd";
import { CalendarOutlined, EnvironmentOutlined, TagsOutlined, InfoCircleOutlined, PictureOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { uldService, ucmService } from "../../../../api/enhancedApi";
import { attachmentApi } from "../../../../api/attachmentApi";
import { ULD_STATUS_NAMES, ULD_STATUS_COLORS, ULD_LOCATION_TYPE_NAMES } from "../constants/uldConstants";
import UldStatusTag from "./UldStatusTag";
import UldCoreStatusTag from './UldCoreStatusTag';
import UldAvailabilityTag from './UldAvailabilityTag';
import UldHistoryTable from "../../UldHistory/components/UldHistoryTable";

// 板流转历史里点 UCM 批次回链到来源报文：动态 import 打破与 UCM 详情抽屉的循环依赖
// （UCM 抽屉里又会内嵌本弹窗看板详情，形成环；用 lazy 让其中一条依赖边变成动态加载）
const UcmInboundDetailDrawer = lazy(() => import("../../UcmManagement/components/UcmInboundDetailDrawer"));

const { Text } = Typography;

const HISTORY_PAGE_SIZE = 50;

const UldDetailModal = ({ 
    visible, 
    onCancel, 
    uldId 
}) => {
    const [loading, setLoading] = useState(false);
    const [uldDetail, setUldDetail] = useState(null);
    const [photoSignedUrls, setPhotoSignedUrls] = useState({});
    const [activeTab, setActiveTab] = useState('detail');
    // 流转历史（懒加载：首次切到「流转历史」Tab 才请求）
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [historyPagination, setHistoryPagination] = useState({ current: 1, pageSize: HISTORY_PAGE_SIZE, total: 0 });
    // 板历史→来源 UCM 报文回链：内嵌的 UCM 详情抽屉
    const [ucmDrawer, setUcmDrawer] = useState({ visible: false, pendingId: null });

    useEffect(() => {
        if (visible && uldId) {
            fetchUldDetail();
        }
    }, [visible, uldId]);

    // 切板 / 重新打开时复位 Tab 与历史缓存，避免看到上一块板的历史
    useEffect(() => {
        setActiveTab('detail');
        setHistoryLoaded(false);
        setHistoryData([]);
        setHistoryPagination({ current: 1, pageSize: HISTORY_PAGE_SIZE, total: 0 });
        setUcmDrawer({ visible: false, pendingId: null });
    }, [uldId, visible]);

    const loadHistory = async (page = 1) => {
        if (!uldId) return;
        setHistoryLoading(true);
        try {
            const resp = await uldService.getUldHistory(uldId, { page, page_size: HISTORY_PAGE_SIZE });
            setHistoryData(resp.data?.items || []);
            setHistoryPagination((prev) => ({ ...prev, current: page, total: resp.data?.total || 0 }));
            setHistoryLoaded(true);
        } catch (error) {
            console.error('获取流转历史失败:', error);
            message.error('获取流转历史失败');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        if (key === 'history' && !historyLoaded) {
            loadHistory(1);
        }
    };

    // 历史行里点 UCM 批次号 → 反查来源报文 → 打开内嵌的 UCM 详情抽屉
    const handleUcmLink = async (batchIdentifier) => {
        try {
            const resp = await ucmService.getUcmInboundByBatch(batchIdentifier);
            const pid = resp.data?.id;
            if (pid) {
                setUcmDrawer({ visible: true, pendingId: pid });
            } else {
                message.info('未找到该批次对应的 UCM 报文');
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                message.info('该批次不是 UCM 报文写入，或报文已删除');
            } else {
                console.error('反查来源报文失败:', error);
                message.error('反查来源报文失败');
            }
        }
    };

    const fetchUldDetail = async () => {
        setLoading(true);
        try {
            const response = await uldService.getUldDetail(uldId);
            setUldDetail(response.data);
            loadPhotoUrls(response.data?.master?.photos);
        } catch (error) {
            console.error('获取ULD详情失败:', error);
            message.error(error.response?.data?.detail || '获取ULD详情失败');
        } finally {
            setLoading(false);
        }
    };

    const loadPhotoUrls = async (photos) => {
        if (!photos || photos.length === 0) return;
        const allPhotos = photos.flatMap((ev) => ev.items || []);
        if (allPhotos.length === 0) return;
        const urls = {};
        await Promise.all(
            allPhotos.map(async (photo) => {
                try {
                    const data = await attachmentApi.getPresignedViewUrl(photo.object_key);
                    urls[photo.object_key] = data.signed_url;
                } catch {
                    urls[photo.object_key] = null;
                }
            })
        );
        setPhotoSignedUrls(urls);
    };

    const handleCancel = () => {
        setUldDetail(null);
        setPhotoSignedUrls({});
        onCancel();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('zh-CN');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('zh-CN');
    };

    const renderLocationInfo = (stock) => {
        if (!stock) return <Tag>未知</Tag>;

        const locationType = stock.current_location_type;
        const locationName = ULD_LOCATION_TYPE_NAMES[locationType] || locationType;

        let locationDetail = "";
        if (locationType === "AIRPORT" && stock.current_airport_iata) {
            locationDetail = stock.current_airport_iata;
        } else if (locationType === "FLIGHT") {
            if (uldDetail?.current_flight_number) {
                // 使用后端返回的格式化航班信息：航班号/起飞日期
                locationDetail = uldDetail.current_flight_number;
            } else if (stock.current_flight_op_id) {
                locationDetail = `航班ID: ${stock.current_flight_op_id}`;
            } else if (stock.transport_identifier) {
                locationDetail = stock.transport_identifier;
            }
        } else if (locationType === "OTHER_LOCATION" && stock.current_other_location) {
            locationDetail = stock.current_other_location;
        }

        return (
            <Space>
                <Tag icon={<EnvironmentOutlined />} color="blue">
                    {locationName}
                </Tag>
                {locationDetail && <span>{locationDetail}</span>}
            </Space>
        );
    };

    return (
        <Modal
            title={`ULD详情 - ${uldDetail?.master?.uld_identifier || '加载中...'}`}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={1100}
            destroyOnHidden
        >
            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={[
                    {
                        key: 'detail',
                        label: '详情',
                        children: (
                            <Spin spinning={loading}>
                {uldDetail && (
                    <>
                        {/* 基础信息 */}
                        <Descriptions 
                            title="基础信息" 
                            bordered 
                            column={2}
                            size="small"
                        >
                            <Descriptions.Item label="ULD识别码" span={2}>
                                <strong style={{ fontSize: 16 }}>
                                    {uldDetail.master.uld_identifier}
                                </strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="ULD类型">
                                {uldDetail.master.uld_type}
                            </Descriptions.Item>
                            <Descriptions.Item label="序列号">
                                {uldDetail.master.serial_number}
                            </Descriptions.Item>
                            <Descriptions.Item label="所有者">
                                {uldDetail.master.owner || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="持有者">
                                {uldDetail.master.holder || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="所有权类型">
                                <Tag color={uldDetail.master.ownership_type === '自有' ? 'green' : 'blue'}>
                                    {uldDetail.master.ownership_type || '-'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="地面处理代理">
                                {uldDetail.master.gha || '-'}
                            </Descriptions.Item>
                            {/* 外放信息 */}
                            <Descriptions.Item label="外放状态">
                                {uldDetail.master.delivered_to_consignee ? (
                                    <Tag color="orange">已外放</Tag>
                                ) : (
                                    <Tag color="green">未外放</Tag>
                                )}
                            </Descriptions.Item>
                            {uldDetail.master.delivered_to_consignee && (
                                <Descriptions.Item label="收货人">
                                    <Text strong>{uldDetail.master.consignee_name || '-'}</Text>
                                </Descriptions.Item>
                            )}
                            {/* 租赁日期信息 - 仅在租赁类型时显示 */}
                            {uldDetail.master.ownership_type === '租赁' && (
                                <>
                                    <Descriptions.Item label="租赁开始日期">
                                        {formatDate(uldDetail.master.lease_start_date)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="租赁结束日期">
                                        {formatDate(uldDetail.master.lease_end_date)}
                                    </Descriptions.Item>
                                </>
                            )}
                        </Descriptions>

                        <Divider />

                        {/* 当前状态 */}
                        {uldDetail.stock && (
                            <>
                                <Descriptions 
                                    title="当前状态" 
                                    bordered 
                                    column={2}
                                    size="small"
                                >
                                    <Descriptions.Item label="核心状态">
                                        <UldCoreStatusTag 
                                            status={uldDetail.stock.core_status} 
                                            description={uldDetail.stock.remarks} 
                                        />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="可用性">
                                        <UldAvailabilityTag 
                                            availability={uldDetail.stock.availability}
                                        />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="当前位置" span={2}>
                                        {renderLocationInfo(uldDetail.stock)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="最后移动时间">
                                        {formatDateTime(uldDetail.stock.last_moved_at)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="状态变更时间">
                                        {formatDateTime(uldDetail.stock.last_status_changed_at)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="备注" span={2}>
                                        {uldDetail.stock.remarks || '-'}
                                    </Descriptions.Item>
                                </Descriptions>

                                <Divider />
                            </>
                        )}

                        {/* 标签信息 */}
                        <div>
                            <div style={{ marginBottom: 12, fontWeight: 'bold', fontSize: 14 }}>
                                <TagsOutlined style={{ marginRight: 8 }} />
                                标签信息
                            </div>
                            <Space size={[0, 8]} wrap>
                                {uldDetail.tags && uldDetail.tags.length > 0 ? (
                                    uldDetail.tags.map((tag) => (
                                        <Tag 
                                            key={tag.id} 
                                            color={tag.color_code || "#2db7f5"}
                                            style={{ margin: '2px' }}
                                        >
                                            {tag.tag_name}
                                            {tag.description && (
                                                <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                                    （{tag.description}）
                                                </span>
                                            )}
                                        </Tag>
                                    ))
                                ) : (
                                    <span style={{ color: '#999' }}>暂无标签</span>
                                )}
                            </Space>
                        </div>

                        <Divider />

                        {/* 照片记录 */}
                        <div>
                            <div style={{ marginBottom: 12, fontWeight: 'bold', fontSize: 14 }}>
                                <PictureOutlined style={{ marginRight: 8 }} />
                                照片记录
                            </div>
                            {(() => {
                                const photoEvents = uldDetail.master.photos || [];
                                if (photoEvents.length === 0) {
                                    return <span style={{ color: '#999' }}>暂无照片记录</span>;
                                }
                                return (
                                    <Timeline
                                        items={photoEvents.map((event) => ({
                                            key: event.event_id,
                                            color: (event.items?.length || 0) > 0 ? 'blue' : 'gray',
                                            children: (
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                                                        <Text strong>{event.title}</Text>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            {event.event_date ? dayjs(event.event_date).format('YYYY-MM-DD') : ''}
                                                        </Text>
                                                    </div>
                                                    {event.description && (
                                                        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                                                            {event.description}
                                                        </div>
                                                    )}
                                                    {event.created_by && (
                                                        <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>
                                                            {event.created_by}
                                                        </div>
                                                    )}
                                                    {(event.items?.length || 0) > 0 ? (
                                                        <Image.PreviewGroup>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                                {event.items.map((photo) => (
                                                                    <div key={photo.object_key} style={{ textAlign: 'center' }}>
                                                                        <Image
                                                                            width={90}
                                                                            height={90}
                                                                            src={photoSignedUrls[photo.object_key] || ''}
                                                                            alt={photo.filename}
                                                                            style={{ objectFit: 'cover', borderRadius: 6 }}
                                                                            placeholder={<Spin size="small" />}
                                                                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F/PQAJpAN42kRbLAAAAABJRU5ErkJggg=="
                                                                        />
                                                                        <div style={{
                                                                            fontSize: 11, color: '#666',
                                                                            maxWidth: 90, overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                        }}>
                                                                            {photo.caption || photo.filename}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </Image.PreviewGroup>
                                                    ) : (
                                                        <Text type="secondary" style={{ fontSize: 12 }}>此记录暂无照片</Text>
                                                    )}
                                                </div>
                                            ),
                                        }))}
                                    />
                                );
                            })()}
                        </div>

                        <Divider />

                        {/* 备注信息 */}
                        {uldDetail.master.remarks && (
                            <div>
                                <div style={{ marginBottom: 12, fontWeight: 'bold', fontSize: 14 }}>
                                    <InfoCircleOutlined style={{ marginRight: 8 }} />
                                    备注信息
                                </div>
                                <div style={{ 
                                    padding: 12, 
                                    background: '#f6f8fa', 
                                    borderRadius: 4,
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {uldDetail.master.remarks}
                                </div>
                            </div>
                        )}

                        {/* 系统信息 */}
                        <Divider />
                        <Descriptions 
                            title="系统信息" 
                            bordered 
                            column={2}
                            size="small"
                        >
                            <Descriptions.Item label="创建时间">
                                {formatDateTime(uldDetail.master.created_at)}
                            </Descriptions.Item>
                            <Descriptions.Item label="更新时间">
                                {formatDateTime(uldDetail.master.updated_at)}
                            </Descriptions.Item>
                        </Descriptions>
                    </>
                )}
                            </Spin>
                        ),
                    },
                    {
                        key: 'history',
                        label: '流转历史',
                        children: (
                            <UldHistoryTable
                                embedded
                                hiddenColumns={['uld_identifier']}
                                data={historyData}
                                loading={historyLoading}
                                pagination={historyPagination}
                                onPageChange={(p) => loadHistory(p)}
                                onUcmLink={handleUcmLink}
                                scrollY={420}
                            />
                        ),
                    },
                ]}
            />

            {/* 板历史→来源 UCM 报文回链：内嵌 UCM 详情抽屉（lazy 加载，打破循环依赖） */}
            <Suspense fallback={null}>
                {ucmDrawer.visible && (
                    <UcmInboundDetailDrawer
                        visible={ucmDrawer.visible}
                        pendingId={ucmDrawer.pendingId}
                        members={[]}
                        onClose={() => setUcmDrawer({ visible: false, pendingId: null })}
                        onChanged={() => { if (activeTab === 'history') loadHistory(historyPagination.current); }}
                    />
                )}
            </Suspense>
        </Modal>
    );
};

export default UldDetailModal; 